import {
  Alert,
  Button,
  Stack,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import red from '@mui/material/colors/red';
import { HeaderBar } from 'components/HeaderBar';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import PageLayout from 'components/PageLayout';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { OrganizationSeatsSummary } from 'features/organizations/components/OrganizationSeatsSummary';
import { useOrganizationDetails } from 'features/organizations/hooks/useOrganizationDetails';
import { useOrganizationState } from 'features/organizations/state/useOrganizationState';
import { OrganizationMembers } from 'features/users/components/OrganizationMembers';
import useScrollPosition from 'hooks/useScrollPosition';
import { defer } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import { useAuthState } from 'state/useAuthState';

export const OrganizationDetails = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    permissions: { canManageOrganization },
  } = useAuthState();
  const { details, isLoading } = useOrganizationDetails();
  const location = useLocation();
  const history = useHistory();
  const { resetOrganizationState } = useOrganizationState();

  useEffect(() => {
    // When user lands on the organization details page, reset the organization state
    // that is used in the purchase/upgrade flow.
    resetOrganizationState();
  }, [resetOrganizationState]);

  const returnRoute = location.state?.fromRoute || '/';

  const hasAvailableSeats = details
    ? details.seats.regular.purchased > details.seats.regular.used ||
      details.seats.pro.purchased > details.seats.pro.used
    : false;

  const containerRef = useRef<Element>();
  const { handleScroll, resetScrollPosition } = useScrollPosition(
    containerRef,
    'organization-details',
  );

  useEffect(() => {
    const unblock = history.block((promptLocation) => {
      // @ts-expect-error pathname does exist on promptLocation
      const newLocationPathname = promptLocation.pathname;
      if (!newLocationPathname.includes('organization/members')) {
        // Defer scroll reset to ensure it runs after navigation completes.
        // Without deferring, React may batch this with navigation updates,
        // causing Recoil state changes (like scroll position) to be skipped or ignored.
        defer(() => resetScrollPosition());
      }
      return true;
    });

    return () => {
      unblock();
    };
  }, [history, resetScrollPosition]);

  if (!canManageOrganization) {
    return <Redirect to="/" />;
  }

  if (isLoading) {
    return <LoadingAndErrors isLoading />;
  }

  return (
    <PageLayout data-cname="OrganizationDetails">
      {{
        content: (
          <>
            <Helmet>
              <title>{t('organization.title')}</title>
            </Helmet>
            <SimplePageLayout
              disableGutters
              ref={containerRef}
              onScroll={handleScroll}
            >
              <Stack spacing={2} padding={2}>
                <Typography variant="h2">
                  {t('organizationSeatsSummary.title')}
                </Typography>
                <OrganizationSeatsSummary seats={details?.seats} showUsed />

                {details?.canPurchaseSeats ? (
                  <Button
                    size="large"
                    fullWidth
                    component={Link}
                    to="/organization/upgrade"
                  >
                    {t('organization.manageMemberships')}
                  </Button>
                ) : (
                  !hasAvailableSeats && (
                    <Alert severity="warning" icon={false}>
                      <Trans i18nKey="organization.purchaseContactSales">
                        To puchase additional seats, please contact us at
                        <MuiLink
                          href="mailto:sales@watchduty.org"
                          target="_blank"
                          rel="noreferrer"
                          sx={{ color: red[500] }}
                        >
                          sales@watchduty.org
                        </MuiLink>
                      </Trans>
                    </Alert>
                  )
                )}

                <Button
                  size="large"
                  fullWidth
                  component={Link}
                  disabled={!hasAvailableSeats}
                  to="/organization/members/create"
                >
                  {t('organization.addMember')}
                </Button>
              </Stack>
              <OrganizationMembers />
            </SimplePageLayout>
          </>
        ),
        headerBar: (
          <HeaderBar title={t('organization.title')} action={returnRoute} />
        ),
      }}
    </PageLayout>
  );
};
